import { Card, Skeleton } from '@startupquickstart/frontend';
import { AlertTriangle, CheckCircle2, CircleAlert } from 'lucide-react';
import { Link } from 'react-router-dom';

import { useEventReadiness, type ReadinessCheck } from '@/components/events/useEventReadiness';

/** Where each readiness rule sends the admin to fix it. */
const CHECK_LINKS: Record<string, (eventId: string) => string> = {
  'no-challenges': (id) => `/events/${id}/challenges`,
  'no-starter-content': (id) => `/events/${id}/challenges`,
  'missing-answers': (id) => `/events/${id}/challenges`,
  'gps-missing-coords': (id) => `/events/${id}/challenges?view=map`,
  'map-missing-coords': (id) => `/events/${id}/challenges?view=map`,
  'invalid-minigame-config': (id) => `/events/${id}/challenges`,
  'qr-missing-code': (id) => `/events/${id}/challenges`,
  'empty-groups': (id) => `/events/${id}/challenges`,
  'no-teams': (id) => `/events/${id}/teams`,
};

function CheckRow({ check, eventId }: { check: ReadinessCheck; eventId: string }) {
  const link = CHECK_LINKS[check.id]?.(eventId);
  const Icon = check.level === 'error' ? CircleAlert : AlertTriangle;
  const tone = check.level === 'error' ? 'text-destructive' : 'text-amber-600';

  const row = (
    <div className="flex items-start gap-2.5 px-4 py-2.5 text-sm transition-colors hover:bg-muted/50">
      <Icon className={`mt-0.5 h-4 w-4 shrink-0 ${tone}`} aria-hidden="true" />
      <span className="flex-1 text-foreground">{check.message}</span>
      {check.count > 1 ? (
        <span className="font-mono text-xs text-muted-foreground">×{check.count}</span>
      ) : null}
    </div>
  );

  return link ? (
    <Link to={link} className="block">
      {row}
    </Link>
  ) : (
    row
  );
}

/**
 * Pre-launch checklist shown on the Overview while an event is in draft or
 * scheduled: every failing readiness rule with a deep link to fix it, or a
 * green all-clear.
 */
export function ReadinessChecklistCard({ eventId }: { eventId: string }) {
  const { data, isLoading } = useEventReadiness(eventId);

  if (isLoading) {
    return (
      <Card className="gap-0 py-0">
        <div className="space-y-2 p-4">
          <Skeleton className="h-5 w-40" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-3/4" />
        </div>
      </Card>
    );
  }

  const readiness = data?.data;
  if (!readiness) return null;

  return (
    <Card className="gap-0 overflow-hidden py-0">
      <div className="flex items-center justify-between border-b px-4 py-3">
        <h3 className="font-mono text-[0.6875rem] font-medium tracking-[0.15em] text-muted-foreground uppercase">
          Ready to launch?
        </h3>
        {readiness.ready ? (
          <span className="inline-flex items-center gap-1.5 text-sm font-medium text-secondary">
            <CheckCircle2 className="h-4 w-4" /> Ready
          </span>
        ) : (
          <span className="text-sm font-medium text-destructive">
            {readiness.checks.filter((check) => check.level === 'error').length} to fix
          </span>
        )}
      </div>
      {readiness.checks.length === 0 ? (
        <p className="px-4 py-4 text-sm text-muted-foreground">
          Everything checks out — this hunt is ready to publish.
        </p>
      ) : (
        <div className="divide-y">
          {readiness.checks.map((check) => (
            <CheckRow key={check.id} check={check} eventId={eventId} />
          ))}
        </div>
      )}
    </Card>
  );
}
